import { MultiComboBox } from "@itandi/itandi-bb-ui/MultiComboBox";
import type { ComponentProps } from "react";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { CurrentFilter } from "~/components/deprecated/CurrentFilter";
import type { DealSearchCondition } from "~/models/Deal";
import type { Status } from "~/models/Status";
import {
	convertToComboBoxOption,
	defaultSelectedOption
} from "~/models/Status";

type Props = {
	statuses: Status[];
};
type MultiComboBoxOption = Exclude<
	ComponentProps<typeof MultiComboBox>["options"][number],
	{ group: string }
>;

export function StatusCombobox({ statuses }: Props) {
	const { setValue, control } = useFormContext<DealSearchCondition>();
	const watchedStatusIds = useWatch({ control, name: "statusId" });

	const selectedStatusOptions = useMemo((): MultiComboBoxOption[] => {
		// statusId が未設定(undefined)の場合のみデフォルト選択を適用する
		// 空配列はユーザーが意図的に全て外した状態なのでそのまま返す
		if (watchedStatusIds === undefined) {
			return defaultSelectedOption(statuses);
		}
		return statuses
			.filter(({ id }) => watchedStatusIds.includes(id))
			.map((s: Status) => convertToComboBoxOption(s));
	}, [watchedStatusIds, statuses]);

	const onStatusChange = useCallback(
		(options: MultiComboBoxOption[]): void => {
			setValue(
				"statusId",
				options.map((option) => Number(option.value))
			);
		},
		[setValue]
	);
	const removeStatusOption = (index: number): void => {
		const removedOptions = selectedStatusOptions.filter((_, i) => i !== index);
		setValue(
			"statusId",
			removedOptions.map((option) => Number(option.value))
		);
	};
	const updatedOptions = selectedStatusOptions.map((option, index) => ({
		...option,
		onRemove: (): void => {
			removeStatusOption(index);
		}
	}));
	return (
		<>
			<MultiComboBox
				onChange={onStatusChange}
				options={statuses.map((s: Status) => convertToComboBoxOption(s))}
				placeholder="ステータス"
				showOptions={false}
				value={selectedStatusOptions}
			/>
			<CurrentFilter options={updatedOptions} scroll={false} />
		</>
	);
}
